import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Search,
  ExternalLink,
  Edit3,
  Trash2,
  Star,
  FileText,
  Folder,
  Calendar,
  AlertCircle,
  MoreVertical,
  CheckCircle2,
  X,
} from "lucide-react";
import "../styles/AdminPanel.css";

const API_BASE_URL = "https://jobs.mpdatahub.com/api";
const CATEGORIES = [
  "Recruitment",
  "Culture",
  "Compliance",
  "Performance",
  "Well-being",
  "DEI",
];

export default function AdminPanel() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [toast, setToast] = useState(null);
  const [stats, setStats] = useState({ total: 0, featured: 0, byCategory: {} });

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/blogs`);
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      const transformedData = data.map((blog) => ({
        ...blog,
        id: blog.id,
        author: blog.author || "Anonymous",
        featured: [true, 1, "1"].includes(blog.featured),
        image: blog.image_url,
        tags: Array.isArray(blog.tags)
          ? blog.tags
          : typeof blog.tags === "string"
            ? JSON.parse(blog.tags || "[]")
            : [],
      }));

      setBlogs(transformedData);
      calculateStats(transformedData);
      setError(null);
    } catch (err) {
      setError("Failed to sync dashboard. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const counts = data.reduce((acc, b) => {
      acc[b.category] = (acc[b.category] || 0) + 1;
      return acc;
    }, {});
    setStats({
      total: data.length,
      featured: data.filter((b) => b.featured).length,
      byCategory: counts,
    });
  };

  const handleToggleFeatured = async (blog) => {
    try {
      const response = await fetch(`${API_BASE_URL}/blogs-update/${blog.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ featured: !blog.featured ? 1 : 0 }),
      });
      if (!response.ok) throw new Error("Update failed");
      await fetchBlogs();
      setToast({
        msg: `Post ${!blog.featured ? "featured" : "unfeatured"}`,
        type: "success",
      });
    } catch (err) {
      setToast({ msg: "Update failed", type: "error" });
    }
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/blogs-destroy/${blogToDelete.id}`,
        { method: "GET" },
      );
      if (!response.ok) throw new Error("Delete failed");
      await fetchBlogs();
      setToast({ msg: "Post removed successfully", type: "success" });
      setShowDeleteModal(false);
    } catch (err) {
      setToast({ msg: "Could not delete post", type: "error" });
    }
  };

  const filteredBlogs = blogs.filter(
    (b) =>
      (selectedCategory === "All" || b.category === selectedCategory) &&
      (b.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.author.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  if (loading)
    return (
      <div className="admin-loader">
        <div className="spinner"></div>
        <p>Synchronizing Data...</p>
      </div>
    );

  return (
    <div className="admin-container">
      {/* Top Navbar */}
      <nav className="admin-nav">
        <div className="nav-brand">
          <div className="brand-logo">
            <FileText size={24} />
          </div>
          <div>
            <h1>Console</h1>
            <span>Project Management v2.0</span>
          </div>
        </div>
        <div className="nav-actions">
          <button className="btn-secondary" onClick={() => navigate("/blog")}>
            <ExternalLink size={18} /> View Site
          </button>
          <button className="btn-primary" onClick={() => navigate("/blog/new")}>
            <Plus size={18} /> New Post
          </button>
        </div>
      </nav>

      <main className="admin-content">
        {/* Stats Section */}
        <section className="stats-row">
          <StatCard
            icon={<FileText color="#6366f1" />}
            label="Total Posts"
            value={stats.total}
          />
          <StatCard
            icon={<Star color="#f59e0b" />}
            label="Featured"
            value={stats.featured}
          />
          <StatCard
            icon={<Folder color="#10b981" />}
            label="Categories"
            value={Object.keys(stats.byCategory).length}
          />
          <StatCard
            icon={<Calendar color="#64748b" />}
            label="Last Updated"
            value={blogs[0]?.date || "N/A"}
          />
        </section>

        {/* Toolbar */}
        <section className="admin-toolbar">
          <div className="search-wrapper">
            <Search className="search-icon" size={18} />
            <input
              placeholder="Search by title, author, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <button
              className={`filter-btn ${selectedCategory === "All" ? "active" : ""}`}
              onClick={() => setSelectedCategory("All")}
            >
              All
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`filter-btn ${selectedCategory === cat ? "active" : ""}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </section>

        {/* Table Area */}
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Post Details</th>
                <th>Category</th>
                <th>Author</th>
                <th>Visibility</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBlogs.map((blog) => (
                <tr key={blog.id}>
                  <td>
                    <div className="post-cell">
                      <img
                        src={blog.image}
                        alt=""
                        onError={(e) =>
                          (e.target.src = "https://via.placeholder.com/100x60")
                        }
                      />
                      <div>
                        <span className="post-name">{blog.title}</span>
                        <p className="post-meta">{blog.date}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="badge">{blog.category}</span>
                  </td>
                  <td>
                    <span className="author-name">{blog.author}</span>
                  </td>
                  <td>
                    <button
                      className={`toggle-star ${blog.featured ? "is-featured" : ""}`}
                      onClick={() => handleToggleFeatured(blog)}
                    >
                      <Star
                        size={16}
                        fill={blog.featured ? "#f59e0b" : "none"}
                      />
                      {blog.featured ? "Featured" : "Standard"}
                    </button>
                  </td>
                  <td className="text-right">
                    <div className="action-row">
                      <button
                        className="icon-btn edit"
                        onClick={() => navigate(`/blog/edit/${blog.id}`)}
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        className="icon-btn delete"
                        onClick={() => {
                          setBlogToDelete(blog);
                          setShowDeleteModal(true);
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-backdrop">
          <div className="modal-box">
            <div className="modal-icon">
              <AlertCircle size={40} color="#ef4444" />
            </div>
            <h3>Delete Post?</h3>
            <p>
              You are about to remove <strong>{blogToDelete?.title}</strong>.
              This cannot be undone.
            </p>
            <div className="modal-btns">
              <button
                className="btn-ghost"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button className="btn-danger" onClick={confirmDelete}>
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`toast ${toast.type}`}>
          {toast.type === "success" ? (
            <CheckCircle2 size={18} />
          ) : (
            <AlertCircle size={18} />
          )}
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="stat-card">
      <div className="stat-icon-bg">{icon}</div>
      <div className="stat-content">
        <span className="stat-label">{label}</span>
        <span className="stat-value">{value}</span>
      </div>
    </div>
  );
}
